import { supabase } from '@/integrations/supabase/client';
import { PropertyFormData, PropertyPhoto, UserProfile } from '@/types/property';
import { getMarketConfig } from '@/lib/markets';

/**
 * Generate a premium, conversion-optimized prose description
 * using the dedicated generate-premium-description edge function.
 * 
 * Returns descriptions keyed by language code.
 */
export async function generatePremiumDescription(
  formData: PropertyFormData,
  photos: PropertyPhoto[],
  profile?: UserProfile | null
): Promise<Record<string, string>> {
  // Convert photos to base64 for vision analysis (up to 5)
  const imagePromises = photos.slice(0, 5).map(async (photo) => {
    if (photo.file) {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => resolve(photo.preview);
        reader.readAsDataURL(photo.file);
      });
    }
    
    if (photo.preview) {
      if (photo.preview.startsWith('data:')) {
        let dataUrl = photo.preview;
        if (dataUrl.includes('application/octet-stream')) {
          dataUrl = dataUrl.replace('application/octet-stream', 'image/jpeg');
        }
        return dataUrl;
      }
      
      try {
        const response = await fetch(photo.preview);
        if (response.ok) {
          const blob = await response.blob();
          let mimeType = blob.type;
          if (!mimeType || mimeType === 'application/octet-stream') mimeType = 'image/jpeg';
          return new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              let result = reader.result as string;
              if (result.includes('application/octet-stream')) {
                result = result.replace('application/octet-stream', mimeType);
              }
              resolve(result);
            };
            reader.readAsDataURL(blob);
          });
        }
      } catch {
        // fall through
      }
      return photo.preview;
    }
    
    return '';
  });

  const images = (await Promise.all(imagePromises)).filter(img => img !== '');

  const sanitizedPrice = formData.price.replace(/[^\d]/g, '');
  const marketCfg = getMarketConfig(formData.market);

  const { data, error } = await supabase.functions.invoke('generate-premium-description', {
    body: {
      propertyData: {
        listingType: formData.listingType,
        category: formData.category,
        type: formData.type,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        squareMeters: formData.squareMeters,
        price: sanitizedPrice,
        address: formData.address,
        city: formData.city,
        features: formData.features,
        condition: formData.condition,
        highlights: formData.highlights?.substring(0, 1000) || '',
        languages: formData.languages,
        furnished: formData.furnished,
      },
      images,
      agentInfo: formData.includeAgentInfo && profile ? {
        phone: profile.phone,
        email: profile.email,
        agencyName: profile.agency_name,
        licenseNumber: profile.license_number,
      } : null,
      userPreferences: {
        tone: formData.descriptionTone || (profile?.description_tone ?? undefined),
        length: formData.descriptionLength || (profile?.description_length ?? undefined),
        customInstructions: formData.customInstructions || (profile?.description_custom_instructions ?? undefined),
        includeHashtags: formData.includeHashtags ?? profile?.include_hashtags ?? true,
        focusFeatures: formData.focusFeatures || (profile?.focus_features as string[] | undefined) || undefined,
        // Market wins, profile is the fallback, so a Dubai listing always renders in AED / sqft
        // regardless of the agent's global profile defaults.
        currency: marketCfg.currency || profile?.currency,
        areaUnit: marketCfg.areaUnit || profile?.area_unit,
        numberFormat: marketCfg.numberFormat || profile?.number_format,
      },
    },
  });

  if (error) {
    console.error('Premium description error:', error);
    throw new Error(error.message || 'Failed to generate premium description');
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data.descriptions as Record<string, string>;
}
